import React, { createContext, useContext, useEffect, useState } from "react";
import { getCookie, setCookie } from "./cookie";
import { messages, defaultLang, supportedLangs } from "./messages";

interface I18nContextProps {
  lang: string;
  setLang: (lang: string) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextProps>({
  lang: defaultLang,
  setLang: () => {},
  t: (key) => key,
});

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lang, setLangState] = useState<string>(defaultLang);

  useEffect(() => {
    try {
      const cookieLang = getCookie("wp-wpml_current_language");

      if (
        typeof cookieLang === "string" &&
        supportedLangs.includes(cookieLang)
      ) {
        setLangState(cookieLang);
      } else {
        const browserLang = navigator.language?.split("-")[0];
        const defaultLanguage = supportedLangs.includes(browserLang)
          ? browserLang
          : defaultLang;
        setLangState(defaultLanguage);
      }
    } catch (err) {
      console.error("Error reading language cookie:", err);
      setLangState(defaultLang);
    }
  }, [setLangState]);

  const setLang = (newLang: string) => {
    if (supportedLangs.includes(newLang)) {
      setCookie("lang", newLang);
      setLangState(newLang);
    }
  };

  const t = (key: string, vars: Record<string, string | number> = {}) => {
    let text = messages[lang]?.[key] || key;
    for (const [k, v] of Object.entries(vars)) {
      text = text.replace(new RegExp(`{${k}}`, "g"), String(v));
    }
    return text;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
